import axiosApiClient from '../api/axios';

/**
 * Convert image path to a blob URL with proper authentication
 * @param {string} imagePath - The image path from the backend
 * @param {string} imageUrl - The direct image URL (for S3)
 * @returns {Promise<string>} - Promise that resolves to a blob URL
 */
export const getImageUrl = async (imagePath, imageUrl = null) => {
    // If we have a direct URL (S3), use it
    if (imageUrl) {
        return imageUrl;
    }

    // For local storage, we need to fetch with authentication
    if (imagePath) {
        try {
            const response = await axiosApiClient.get(`/api/images?path=${encodeURIComponent(imagePath)}`, {
                responseType: 'blob'
            });

            // Create a blob URL from the response
            const blob = new Blob([response.data], {type: response.headers['content-type']});
            return URL.createObjectURL(blob);
        } catch (error) {
            console.error('Failed to load image:', error);
            return '/placeholder-image.jpg'; // Fallback image
        }
    }

    return '/placeholder-image.jpg'; // Default fallback
};

/**
 * Clean up blob URLs to prevent memory leaks
 * @param {string} blobUrl - The blob URL to revoke
 */
export const revokeImageUrl = (blobUrl) => {
    if (blobUrl && blobUrl.startsWith('blob:')) {
        URL.revokeObjectURL(blobUrl);
    }
};

/**
 * Get image URL for use in img src (with fallback)
 * @param {string} imagePath - The image path from the backend
 * @param {string} imageUrl - The direct image URL (for S3)
 * @param {string} publicImageUrl - The public image URL (no auth required)
 * @returns {string} - URL string for img src
 */
export const getImageSrcUrl = (imagePath, imageUrl = null, publicImageUrl = null) => {
    // Prefer public URL if available (no authentication required)
    if (publicImageUrl) {
        return publicImageUrl;
    }

    // If we have a direct URL (S3), use it
    if (imageUrl) {
        return imageUrl;
    }

    // For local storage, construct the API URL
    if (imagePath) {
        return `/api/images?path=${encodeURIComponent(imagePath)}`;
    }

    return '/placeholder-image.jpg'; // Default fallback
};
